import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, RotateCcw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { WorkCard } from "@/components/staff/MyWorks";

const DEPT_FILTERS = ["Tous", "RH", "Comptabilité & Paie", "Fiscalité", "Direction"];

export default function DeptReview({ user, scope }) {
  const [works, setWorks] = useState(null);
  const [filter, setFilter] = useState("Tous");
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/works").then((r) => setWorks(r.data));
  useEffect(() => {
    load().catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, []);

  const review = async (w, value) => {
    setBusyId(w.id);
    try {
      await api.patch(`/works/${w.id}`, { review: value });
      toast.success(value === "parfait" ? "Travail marqué « parfait »." : "Travail marqué « à corriger ».");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const canReview = (w) =>
    user.role === "gerant" || (user.role === "responsable" && w.author_id !== user.user_id);

  const visible = (works || []).filter(
    (w) => filter === "Tous" || w.dept === filter
  );

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {scope === "all" &&
          DEPT_FILTERS.map((d) => (
            <button
              key={d}
              onClick={() => setFilter(d)}
              data-testid={`dept-filter-${d}`}
              className={`rounded-full px-3.5 py-1.5 text-xs font-bold transition-all ${
                filter === d ? "bg-korban-navy text-korban-gold" : "bg-white text-slate-600 ring-1 ring-slate-200 hover:ring-korban-blue"
              }`}
            >
              {d}
            </button>
          ))}
        <p className="ml-auto text-xs font-semibold text-slate-500">
          {scope === "dept" ? `Département ${user.dept}` : `${(works || []).length} travaux`}
        </p>
      </div>

      <div className="mt-5 space-y-3" data-testid="dept-review-list">
        {!works ? (
          <p className="flex items-center gap-2 text-slate-500"><Loader2 size={16} className="animate-spin" /> Chargement…</p>
        ) : visible.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-slate-500" data-testid="dept-review-empty">
            Aucun travail à afficher.
          </p>
        ) : (
          visible.map((w) => (
            <WorkCard key={w.id} w={w}>
              {canReview(w) && (
                <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                  <button
                    onClick={() => review(w, "parfait")}
                    disabled={busyId === w.id}
                    data-testid={`review-parfait-${w.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3.5 py-2 text-xs font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {busyId === w.id ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Parfait
                  </button>
                  <button
                    onClick={() => review(w, "corriger")}
                    disabled={busyId === w.id}
                    data-testid={`review-corriger-${w.id}`}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3.5 py-2 text-xs font-bold text-red-600 ring-1 ring-red-200 transition-all hover:-translate-y-0.5 hover:bg-red-50 disabled:opacity-50"
                  >
                    <RotateCcw size={13} /> À corriger
                  </button>
                </div>
              )}
            </WorkCard>
          ))
        )}
      </div>
    </div>
  );
}
